import { useState, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronsUpDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { Skeleton } from "components/common/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/TooltipV3";
import { useUserCatalog } from "hooks/useSession";
import { useWorkspaceIdentifiers } from "hooks/useWorkspace";
import { useWorkspaceSettings } from "hooks/useWorkspace/useWorkspaceSettings";
import { useWorkspaceImage } from "hooks/useWorkspace/useWorkspaceImage";
import { useWorkspaceAuth } from "hooks/useWorkspace/useWorkspaceAuth";
import { PATH_WORKSPACE } from "config";
import { cn } from "utils/cn";
import { WorkspaceDropdownProps, WorkspaceRow } from "./WorkspaceDropdown.types";
import { useDispatch } from "react-redux";
import { setActiveWorkspace, setLastAccessedWorkspace } from "store/reducers/workspace";
import { WorkspaceRole } from "hooks/useWorkspace/workspace.types";
import { WorkspaceProductShellMenu, WorkspaceSettingsShellMenu } from "./components";
import { Permissions } from "utils/permissions/permissionConstants";
import { useCreateProjectLauncher } from "hooks/project";
import { isWorkspaceSettingsPath } from "utils/routing/workspaceRouteUtils";
import { getVisibleWorkspaceSettingsCategories } from "pages/settings/workspace/workspaceSettingsVisibility";
import { getWorkspaceTriggerSubtitle } from "./workspaceDropdown.utils";

export const WorkspaceDropdown = ({
  className = "",
  collapsed = false,
}: WorkspaceDropdownProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: urlProjectId } = useParams<{ projectId?: string }>();
  const dispatch = useDispatch();
  const { launchCreateProject, createProjectFlowElement } = useCreateProjectLauncher();
  const [isOpen, setIsOpen] = useState(false);
  const isSettingsShell = isWorkspaceSettingsPath(location.pathname);

  const { workspaceId } = useWorkspaceIdentifiers();
  const { data: catalog, isLoading } = useUserCatalog();
  const { canWorkspace, isLoading: isWorkspaceAuthLoading } = useWorkspaceAuth(workspaceId || "");

  const { workspace: currentWorkspaceDetail } = useWorkspaceSettings(workspaceId || "");
  const currentWorkspace = catalog?.workspaces.find((w) => w.workspace_id === workspaceId);
  // The catalog's image shows while the workspace's own request loads. Once loaded, the
  // workspace's own image path decides, and null or "" means its image was removed. A missing
  // field is not an answer, so it keeps the catalog's image.
  const currentWorkspaceImagePath =
    currentWorkspaceDetail && currentWorkspaceDetail.imagePath !== undefined
      ? currentWorkspaceDetail.imagePath
      : currentWorkspace?.image_path;
  const {
    image: currentWsImage,
    hasImagePath: hasCurrentWsImagePath,
    shouldShowImage: shouldShowCurrentWsImage,
  } = useWorkspaceImage(workspaceId || "", currentWorkspaceImagePath);

  const workspaces = useMemo((): WorkspaceRow[] => {
    if (!catalog?.workspaces) return [];

    return catalog.workspaces.map((workspace) => {
      const normalizedWorkspaceRole = workspace.role.toLowerCase();
      let role = normalizedWorkspaceRole;

      if (
        normalizedWorkspaceRole === WorkspaceRole.OWNER ||
        normalizedWorkspaceRole === WorkspaceRole.ADMIN ||
        normalizedWorkspaceRole === WorkspaceRole.MEMBER ||
        normalizedWorkspaceRole === WorkspaceRole.GUEST
      ) {
        role = normalizedWorkspaceRole;
      } else {
        role = WorkspaceRole.GUEST;
      }

      return {
        id: workspace.workspace_id,
        name: workspace.name,
        role,
        imagePath: workspace.image_path,
        projects:
          workspace.projects?.map((project) => ({
            id: project.project_id,
            name: project.name,
            productionType: project.production_type,
            archived: project.archived,
            trialExpiryDate: project.trial_expiry_date,
          })) || [],
      };
    });
  }, [catalog?.workspaces]);

  const sortedWorkspaces = useMemo(() => {
    if (!workspaces.length) return workspaces;
    const activeIndex = workspaces.findIndex((w) => w.id === workspaceId);
    if (activeIndex === -1 || activeIndex === 0) return workspaces;
    const activeWorkspace = workspaces[activeIndex];
    const otherWorkspaces = workspaces.filter((w) => w.id !== workspaceId);
    return [activeWorkspace, ...otherWorkspaces];
  }, [workspaces, workspaceId]);

  const activeWorkspaceRow = useMemo(
    () => sortedWorkspaces.find((workspace) => workspace.id === workspaceId) ?? null,
    [sortedWorkspaces, workspaceId]
  );

  const canCreateProject =
    !isWorkspaceAuthLoading &&
    Boolean(workspaceId) &&
    canWorkspace(Permissions.WORKSPACE_PROJECT_CREATE);

  const canAccessWorkspaceSettings =
    !isWorkspaceAuthLoading && Boolean(workspaceId) && canWorkspace(Permissions.WORKSPACE_READ);

  const visibleSettingsCategories = useMemo(
    () => getVisibleWorkspaceSettingsCategories(canWorkspace),
    [canWorkspace]
  );

  const currentProjectName = useMemo(() => {
    if (!urlProjectId || urlProjectId === "0" || !activeWorkspaceRow) {
      return null;
    }
    return activeWorkspaceRow.projects.find((project) => project.id === urlProjectId)?.name ?? null;
  }, [urlProjectId, activeWorkspaceRow]);

  const triggerSubtitle = useMemo(
    () =>
      getWorkspaceTriggerSubtitle({
        isSettingsShell,
        currentProjectName,
        projectCount: activeWorkspaceRow?.projects.length ?? 0,
      }),
    [isSettingsShell, currentProjectName, activeWorkspaceRow?.projects.length]
  );

  const handleWorkspaceSettings = (targetWorkspaceId: string) => {
    dispatch(setActiveWorkspace(targetWorkspaceId));
    dispatch(setLastAccessedWorkspace(targetWorkspaceId));
    navigate(PATH_WORKSPACE.workspaceSettings.replace(":workspaceId", targetWorkspaceId));
    setIsOpen(false);
  };

  const handleWorkspaceSwitch = (targetWorkspaceId: string) => {
    dispatch(setActiveWorkspace(targetWorkspaceId));
    dispatch(setLastAccessedWorkspace(targetWorkspaceId));
    navigate(PATH_WORKSPACE.workspaceProjects.replace(":workspaceId", targetWorkspaceId));
    setIsOpen(false);
  };

  const handleAllProjects = () => {
    if (!workspaceId) {
      return;
    }
    navigate(PATH_WORKSPACE.workspaceProjects.replace(":workspaceId", workspaceId));
    setIsOpen(false);
  };

  const handleOpenProject = (targetWorkspaceId: string, projectId: string) => {
    navigate(`/workspaces/${targetWorkspaceId}/projects/${projectId}/script`);
    setIsOpen(false);
  };

  const currentWorkspaceRole = currentWorkspace?.role?.toLowerCase();
  // The catalog names the workspace while its own request loads; "Workspace" only when neither does.
  const currentWorkspaceName =
    currentWorkspaceDetail?.name || currentWorkspace?.name || "Workspace";
  const currentInitial = (currentWorkspaceName.trim()[0] || "W").toUpperCase();

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex min-h-8 w-full items-center gap-2.5 px-2 py-1.5",
          collapsed && "justify-center px-0",
          className
        )}
      >
        <Skeleton className="h-6 w-6 shrink-0 rounded-md" />
        {!collapsed ? <Skeleton className="h-4 w-28" /> : null}
      </div>
    );
  }

  const trigger = (
    <button
      type="button"
      aria-label={`Workspace: ${currentWorkspaceName}. ${triggerSubtitle}`}
      className={cn(
        "flex min-h-8 w-full min-w-0 items-center gap-2.5 rounded-[7px] px-2 py-1.5 text-left text-[13px] text-chrome-foreground transition-colors hover:bg-chrome-muted",
        "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary",
        collapsed && "justify-center px-0",
        className
      )}
    >
      <span className="grid h-6 w-6 shrink-0 place-items-center overflow-hidden rounded-md bg-chrome-foreground text-chrome">
        {hasCurrentWsImagePath && currentWsImage.isLoading ? (
          <Skeleton className="h-full w-full rounded-md" />
        ) : shouldShowCurrentWsImage ? (
          <img
            src={currentWsImage.src}
            alt=""
            className="h-full w-full object-cover"
            decoding="async"
          />
        ) : (
          <span className="text-[11px] font-semibold leading-none">{currentInitial}</span>
        )}
      </span>
      {!collapsed ? (
        <>
          <span className="min-w-0 flex-1 truncate font-semibold">{currentWorkspaceName}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-chrome-muted-foreground" aria-hidden />
        </>
      ) : null}
    </button>
  );

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        {collapsed ? (
          <Tooltip delayDuration={150}>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10}>
              {currentWorkspaceName}
            </TooltipContent>
          </Tooltip>
        ) : (
          <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        )}

        <DropdownMenuContent
          className="w-72 rounded-lg"
          side={collapsed ? "right" : "bottom"}
          align="start"
          sideOffset={4}
          collisionPadding={12}
        >
          {sortedWorkspaces.length === 0 ? (
            <DropdownMenuItem disabled>
              <div className="text-sm text-muted-foreground">No workspaces available</div>
            </DropdownMenuItem>
          ) : isSettingsShell ? (
            <WorkspaceSettingsShellMenu
              activeWorkspaceId={workspaceId}
              currentWorkspaceName={currentWorkspaceName}
              currentWorkspaceRole={currentWorkspaceRole}
              currentWorkspaceImagePath={currentWorkspaceImagePath}
              settingsCategories={visibleSettingsCategories}
              activePathname={location.pathname}
              workspaces={sortedWorkspaces}
              onAllProjects={handleAllProjects}
              onNavigateSettings={(href) => {
                navigate(href);
                setIsOpen(false);
              }}
              onSwitchWorkspace={handleWorkspaceSwitch}
              onClose={() => setIsOpen(false)}
            />
          ) : (
            <WorkspaceProductShellMenu
              activeWorkspaceId={workspaceId}
              currentWorkspaceName={currentWorkspaceName}
              currentWorkspaceRole={currentWorkspaceRole}
              currentWorkspaceImagePath={currentWorkspaceImagePath}
              activeWorkspace={activeWorkspaceRow}
              currentProjectId={urlProjectId && urlProjectId !== "0" ? urlProjectId : null}
              workspaces={sortedWorkspaces}
              canCreateProject={canCreateProject}
              canAccessWorkspaceSettings={canAccessWorkspaceSettings}
              onAllProjects={handleAllProjects}
              onCreateProject={launchCreateProject}
              onWorkspaceSettings={() => {
                if (workspaceId) {
                  handleWorkspaceSettings(workspaceId);
                }
              }}
              onOpenProject={handleOpenProject}
              onSwitchWorkspace={handleWorkspaceSwitch}
              onClose={() => setIsOpen(false)}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {createProjectFlowElement}
    </>
  );
};
